/* Phase 8 Slice 5 — Audit Timeline UI.
 *
 * Cursor-paginated timeline of every audit entry the tenant can see.
 * Filters by kind, actor, correlation_id, target, full-text. Owner can
 * export the filtered subset as CSV. See ADR-0040.
 */
import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChevronDown, ChevronUp, User as UserIcon, Bot, Webhook,
  Settings as SettingsIcon, Download, Search, Filter,
  AlertTriangle, AlertOctagon, Info,
} from "lucide-react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";

const KINDS = [
  "user_action", "ai_action", "api_action", "approval",
  "publish", "budget_change", "crm_sync", "data_import",
  "data_export", "system",
];

const QUICK = [
  { key: "ai_today", labelKey: "audit.quick_ai_today",
    fallback: "AI-Aktionen heute",
    apply: () => ({
      kind: ["ai_action"],
      since: new Date(new Date().toDateString()).toISOString(),
    }) },
  { key: "budget_7d", labelKey: "audit.quick_budget_7d",
    fallback: "Budgetänderungen 7T",
    apply: () => ({
      kind: ["budget_change"],
      since: new Date(Date.now() - 7 * 86400_000).toISOString(),
    }) },
  { key: "critical", labelKey: "audit.quick_critical",
    fallback: "Nur kritische",
    apply: () => ({ severity: "critical" }) },
];

function ActorIcon({ kind }) {
  if (kind === "agent") return <Bot className="h-4 w-4 text-violet-600" />;
  if (kind === "webhook") return <Webhook className="h-4 w-4 text-sky-600" />;
  if (kind === "system") return <SettingsIcon className="h-4 w-4 text-muted-foreground" />;
  return <UserIcon className="h-4 w-4 text-foreground" />;
}

function SeverityBadge({ severity, t }) {
  if (severity === "critical") {
    return (
      <Badge variant="outline"
                className="border-rose-300 text-rose-700 bg-rose-50">
        <AlertOctagon className="h-3 w-3 mr-1" />
        {t("audit.severity_critical", "kritisch")}
      </Badge>
    );
  }
  if (severity === "warning") {
    return (
      <Badge variant="outline"
                className="border-amber-300 text-amber-700 bg-amber-50">
        <AlertTriangle className="h-3 w-3 mr-1" />
        {t("audit.severity_warning", "warnung")}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      <Info className="h-3 w-3 mr-1" />
      {t("audit.severity_info", "info")}
    </Badge>
  );
}

function TimelineRow({ entry, t, onSetCorrelation }) {
  const [open, setOpen] = useState(false);
  const actor = entry.actor || {};
  return (
    <div
      data-testid={`audit-row-${entry.id}`}
      className="border-b border-border/60 px-3 py-3 hover:bg-muted/30"
    >
      <button
        type="button"
        data-testid={`audit-row-toggle-${entry.id}`}
        onClick={() => setOpen(v => !v)}
        className="flex w-full items-start gap-3 text-left"
      >
        <div className="pt-0.5"><ActorIcon kind={actor.kind} /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground tabular-nums">
              {entry.occurred_at?.slice(0, 19).replace("T", " ")}
            </span>
            <span className="text-xs font-medium uppercase tracking-wide
                                    text-violet-700">{entry.kind}</span>
            <SeverityBadge severity={entry.severity} t={t} />
            {entry.correlation_id && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSetCorrelation(entry.correlation_id);
                }}
                data-testid={`audit-corr-chip-${entry.id}`}
                className="text-[10px] font-mono px-1.5 py-0.5 rounded
                                bg-muted/60 hover:bg-muted text-muted-foreground"
              >
                {entry.correlation_id.slice(0, 12)}…
              </button>
            )}
          </div>
          <div className="mt-0.5 text-sm">{entry.summary}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {actor.display_name || actor.kind}
            {entry.target?.kind ? ` → ${entry.target.kind}` : ""}
            {entry.target?.id ? ` #${entry.target.id.slice(0, 8)}` : ""}
          </div>
        </div>
        <div className="pt-0.5">
          {open
            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
            : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>
      {open && (
        <pre
          data-testid={`audit-row-metadata-${entry.id}`}
          className="mt-2 ml-7 text-[11px] font-mono bg-muted/40
                          rounded-md p-2 max-h-64 overflow-auto"
        >
{JSON.stringify(entry.metadata, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default function AuditPage() {
  const t = useT();
  const [searchParams] = useSearchParams();
  // Phase 8 Slice 4.2 — accept ?target.kind=…&target.id=… cross-links.
  const initialTargetKind = searchParams.get("target.kind") || "";
  const initialTargetId = searchParams.get("target.id") || "";
  const [kind, setKind] = useState([]);
  const [correlationId, setCorrelationId] = useState("");
  const [qText, setQText] = useState("");
  const [targetKind] = useState(initialTargetKind);
  const [targetId] = useState(initialTargetId);
  const [cursorPages, setCursorPages] = useState([]); // stack of cursors

  const filterKey = useMemo(() => JSON.stringify({
    kind, correlationId, qText, targetKind, targetId,
  }), [kind, correlationId, qText, targetKind, targetId]);

  const { data, isLoading } = useQuery({
    queryKey: ["audit-entries", filterKey, cursorPages.length],
    queryFn: async () => {
      const params = {};
      if (kind.length) params.kind = kind;
      if (correlationId) params.correlation_id = correlationId;
      if (qText) params.q = qText;
      if (targetKind) params.target_kind = targetKind;
      if (targetId) params.target_id = targetId;
      if (cursorPages.length) params.cursor = cursorPages[cursorPages.length - 1];
      params.limit = 50;
      const r = await api.get("/audit/entries", { params });
      return r.data;
    },
    keepPreviousData: true,
  });

  const items = data?.items || [];

  const toggleKind = (k) => {
    setKind(prev => prev.includes(k)
      ? prev.filter(x => x !== k) : [...prev, k]);
    setCursorPages([]);
  };

  const downloadCsv = () => {
    const params = new URLSearchParams();
    kind.forEach(k => params.append("kind", k));
    if (correlationId) params.append("correlation_id", correlationId);
    if (qText) params.append("q", qText);
    const url = `${api.defaults.baseURL}/audit/entries/_export.csv?${params}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-4" data-testid="audit-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {t("audit.title", "Audit-Verlauf")}
        </h1>
        <Button
          variant="outline" size="sm"
          data-testid="audit-export-csv"
          onClick={downloadCsv}
        >
          <Download className="h-4 w-4 mr-1" />
          {t("audit.export_csv", "Als CSV exportieren")}
        </Button>
      </div>

      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-wrap gap-2 items-center"
                 data-testid="audit-kind-filter">
            <Filter className="h-4 w-4 text-muted-foreground" />
            {KINDS.map(k => (
              <button
                key={k}
                type="button"
                data-testid={`audit-kind-${k}`}
                onClick={() => toggleKind(k)}
                className={`text-xs px-2 py-1 rounded-md border transition
                  ${kind.includes(k)
                    ? "border-violet-400 bg-violet-50 text-violet-800"
                    : "border-border text-muted-foreground hover:text-foreground"}`}
              >
                {k}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="h-4 w-4 absolute left-2 top-2.5
                                          text-muted-foreground" />
              <Input
                data-testid="audit-q-input"
                value={qText}
                onChange={e => { setQText(e.target.value);
                                          setCursorPages([]); }}
                placeholder={t("audit.search_placeholder",
                                       "Suchbegriff…")}
                className="pl-8"
              />
            </div>
            <Input
              data-testid="audit-correlation-input"
              value={correlationId}
              onChange={e => { setCorrelationId(e.target.value);
                                        setCursorPages([]); }}
              placeholder={t("audit.correlation_placeholder",
                                     "correlation_id")}
              className="flex-1 min-w-[200px] font-mono text-xs"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4">
        <Card data-testid="audit-timeline">
          <CardContent className="p-0">
            {isLoading && (
              <div className="p-6 text-sm text-muted-foreground">
                {t("audit.loading", "Lädt…")}
              </div>
            )}
            {!isLoading && items.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground italic"
                    data-testid="audit-empty">
                {t("audit.empty",
                    "Keine Audit-Einträge mit diesen Filtern.")}
              </div>
            )}
            {items.map(entry => (
              <TimelineRow
                key={entry.id} entry={entry} t={t}
                onSetCorrelation={(c) => { setCorrelationId(c);
                                                            setCursorPages([]); }}
              />
            ))}
            {data?.next_cursor && (
              <div className="p-3 text-center">
                <Button
                  variant="outline" size="sm"
                  data-testid="audit-load-more"
                  onClick={() =>
                    setCursorPages(prev => [...prev, data.next_cursor])}
                >
                  {t("audit.load_more", "Weitere laden")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        <Card data-testid="audit-quick-filters">
          <CardContent className="p-3 space-y-2">
            <div className="text-xs uppercase tracking-wide
                                    text-muted-foreground">
              {t("audit.quick_filters", "Schnellfilter")}
            </div>
            {QUICK.map(qf => (
              <button
                key={qf.key}
                type="button"
                data-testid={`audit-quick-${qf.key}`}
                onClick={() => {
                  const patch = qf.apply();
                  if (patch.kind) setKind(patch.kind);
                  setCursorPages([]);
                }}
                className="block w-full text-left text-xs px-2 py-1.5
                                rounded-md hover:bg-muted/60 border border-border"
              >
                {t(qf.labelKey, qf.fallback)}
              </button>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
